/* Owner: WP8. */
/* BUBBO cart drawer behaviors. Two jobs only, both delegated so they survive Horizon's morphs:
   1. 'bubbo:added' (dispatched by bubbo.js after a successful add) -> the newest line's mascot springs up behind the
      drawer header ([data-bb-cart-peek].is-peeking) once the drawer's slide-in is over, blinks once and stays.
   2. Upsell "+" -> window.Shopify.actions.updateCart({ lines: [{ merchandiseId, quantity: 1 }] }). Horizon's
      cart-items-component re-renders the drawer through its normal path; nothing here touches cart markup. A failed
      add (userErrors, rejection) writes one line into the row's polite live region and leaves focus on "+".
   No import/export: the store loads this file as a module (bubbo-head), the harness as a classic script.
   No window scroll/resize listeners, no rAF loops. */
(() => {
  if (window.BubboCart) return;
  const doc = document;
  const root = doc.documentElement;
  const PEEK_MS = 420;
  const mq = matchMedia('(prefers-reduced-motion: reduce)');
  const reduced = () =>
    window.Bubbo ? !!window.Bubbo.reducedMotion : mq.matches || root.classList.contains('bb-static') || root.classList.contains('shopify-design-mode');

  const drawer = () => doc.querySelector('theme-drawer#cart-drawer');
  const dialog = () => drawer()?.querySelector('dialog') || null;
  const isOpen = () => !!drawer()?.hasAttribute('open');
  const peekEl = () => dialog()?.querySelector('[data-bb-cart-peek]') || null;
  const keyOf = (peek) => peek?.querySelector('[data-bb-peek-key]')?.getAttribute('data-bb-peek-key') || null;

  let armed = false; /* an add happened in this page view: the peek shows (and stays) from now on */
  let lastKey = null;
  let upsellAt = 0; /* time of the last upsell add still waiting for Horizon's re-render */
  let addAt = 0; /* time of the last 'shopify:cart:lines-update' with action 'add' */
  const ADD_WINDOW = 2000;
  let springAt = 0;
  let springKey = null;
  let showTimer = 0;
  let blinkTimer = 0;
  let upsellTimer = 0;

  /* Run fn once the drawer's own slide-in animation is over (Horizon: .theme-drawer__dialog--opening). */
  function afterOpen(fn) {
    clearTimeout(showTimer);
    let done = false;
    const go = () => {
      if (done) return;
      done = true;
      clearTimeout(showTimer);
      fn();
    };
    let slide = null;
    try {
      slide = dialog()?.getAnimations?.().find((a) => /drawer-slide/.test(a.animationName || '')) || null;
    } catch {}
    if (slide) slide.finished.then(go, go);
    showTimer = setTimeout(go, slide ? 700 : 60);
  }

  /* Hide at once, then spring the (current) newest mascot up and blink once. Reduced motion: shown at rest. */
  function spring() {
    const peek = peekEl();
    clearTimeout(blinkTimer);
    if (!peek) return;
    lastKey = keyOf(peek);
    springAt = Date.now();
    springKey = lastKey;
    if (reduced()) {
      clearTimeout(showTimer);
      peek.classList.add('is-peeking');
      return;
    }
    /* snap out of sight (no transition), then spring up from behind the rule */
    peek.classList.add('is-resetting');
    peek.classList.remove('is-peeking');
    void peek.offsetWidth;
    peek.classList.remove('is-resetting');
    afterOpen(() => {
      const p = peekEl();
      if (!p) return;
      void p.offsetWidth;
      p.classList.add('is-peeking');
      blinkTimer = setTimeout(() => {
        try { window.Bubbo?.blink?.(p.querySelector('.bb-mascot')); } catch {}
      }, PEEK_MS);
    });
  }

  /* A newly arrived peek (the previous newest line had no mascot) shows at rest, no spring. */
  function showAtRest(peek) {
    if (peek.classList.contains('is-peeking')) return;
    peek.classList.add('is-resetting', 'is-peeking');
    void peek.offsetWidth;
    peek.classList.remove('is-resetting');
  }

  /* Horizon morphs the drawer after every cart change. The peek wrapper carries data-skip-node-update, so its
     .is-peeking state survives and only the face inside is swapped. When the newest mascot changes while the drawer is
     open, it springs again ONLY if an add caused it (upsell "+", or an add from the page behind a drawer left open).
     Any other change (removing the newest line) swaps the face in place: the peek is decor, never a cheer on a removal. */
  let observed = null;
  const mo = new MutationObserver(() => {
    const peek = peekEl();
    const key = keyOf(peek);
    if (key === lastKey) return;
    lastKey = key;
    if (!peek || !armed || !isOpen()) return;
    const fromAdd = !!upsellAt || Date.now() - addAt < ADD_WINDOW;
    if (upsellAt) {
      upsellAt = 0;
      clearTimeout(upsellTimer);
    }
    if (fromAdd) spring();
    else showAtRest(peek);
  });
  function observe() {
    const d = dialog();
    if (!d || d === observed) return;
    mo.disconnect();
    mo.observe(d, { subtree: true, childList: true, attributes: true, attributeFilter: ['data-bb-peek-key'] });
    observed = d;
    lastKey = keyOf(peekEl());
  }

  /* 20/10: the peek is part of the bag's look. Whenever the drawer opens with a mascot line (bag icon, set box, any add),
     it rises from behind the header rule and stays, not only after a bubbo.js add. */
  function watchOpen() {
    const dr = drawer();
    if (!dr || dr.__bbOpenWatch) return;
    dr.__bbOpenWatch = true;
    new MutationObserver(() => {
      if (!isOpen()) return;
      observe();
      armed = true;
      const p = peekEl();
      if (p && !p.classList.contains('is-peeking')) spring();
    }).observe(dr, { attributes: true, attributeFilter: ['open'] });
  }
  watchOpen();
  doc.addEventListener('DOMContentLoaded', watchOpen);

  doc.addEventListener('bubbo:added', () => {
    observe();
    armed = true;
    /* An upsell add: wait for the re-render to bring the new mascot (the observer springs it), with a fallback. */
    if (upsellAt && Date.now() - upsellAt < 4000) {
      clearTimeout(upsellTimer);
      upsellTimer = setTimeout(() => {
        if (!upsellAt) return;
        upsellAt = 0;
        if (isOpen()) spring();
      }, 1500);
      return;
    }
    /* The observer may already have sprung this face for the same add (drawer left open on desktop). */
    if (Date.now() - springAt < 900 && keyOf(peekEl()) === springKey && peekEl()?.classList.contains('is-peeking')) return;
    spring();
  });

  /* Horizon's ThemeDrawer.open() on an already open drawer replays the slide-in (bringToFront). bubbo.js opens the
     drawer after every add, so while an add lands on an open drawer, mark the dialog to suppress that replay. */
  doc.addEventListener('shopify:cart:lines-update', (event) => {
    if (event.action !== 'add') return;
    addAt = Date.now();
    if (!isOpen()) return;
    const d = dialog();
    d?.setAttribute('data-bb-stay', '');
    clearTimeout(d?._bbStay);
    if (d) d._bbStay = setTimeout(() => d.removeAttribute('data-bb-stay'), 2500);
  }, true);

  /* Upsell "+": the row is a real form posting to /cart/add (works without JS); with Shopify.actions it stays in the drawer. */
  function settleStay() {
    const d = dialog();
    if (!d) return;
    clearTimeout(d._bbStay);
    d._bbStay = setTimeout(() => d.removeAttribute('data-bb-stay'), 1500);
  }
  doc.addEventListener('submit', (event) => {
    const form = event.target instanceof Element ? event.target.closest('[data-bb-upsell-form]') : null;
    if (!form) return;
    const update = window.Shopify?.actions?.updateCart;
    if (typeof update !== 'function') return; /* no Shopify.actions: let the form post natively */
    event.preventDefault();
    const btn = form.querySelector('[data-bb-upsell-add]');
    const id = btn?.getAttribute('data-variant-id');
    if (!btn || !id || btn.classList.contains('is-pending')) return;

    const hadFocus = doc.activeElement === btn;
    const status = form.closest('[data-bb-cart-upsell]')?.querySelector('[data-bb-upsell-status]') || null;
    if (status) status.textContent = '';
    btn.classList.add('is-pending');
    btn.setAttribute('aria-busy', 'true');
    observe();
    armed = true;
    upsellAt = Date.now();
    dialog()?.setAttribute('data-bb-stay', '');

    const reset = () => {
      upsellAt = 0;
      if (btn.isConnected) {
        btn.classList.remove('is-pending');
        btn.removeAttribute('aria-busy');
      }
    };
    /* Shopify's own message when it sends one (e.g. sold out since render), else the stock "Error adding to cart". */
    const fail = (message) => {
      reset();
      dialog()?.removeAttribute('data-bb-stay');
      if (!status?.isConnected) return;
      const text = (typeof message === 'string' && message.trim()) || status.getAttribute('data-error') || '';
      /* clear first so the same message is announced again on a second failed try */
      status.textContent = '';
      setTimeout(() => {
        status.textContent = text;
        /* keep the line in view above the sheet (the items area is the scroller); focus does not move */
        try { status.scrollIntoView({ block: 'nearest', behavior: reduced() ? 'auto' : 'smooth' }); } catch {}
      }, 60);
    };
    /* Safety: if the re-render never replaces the row, give the button back. */
    const safety = setTimeout(reset, 8000);

    let request;
    try {
      request = update({ lines: [{ merchandiseId: `gid://shopify/ProductVariant/${id}`, quantity: 1 }] });
    } catch (error) {
      request = Promise.reject(error);
    }
    Promise.resolve(request)
      .then((result) => {
        if (result?.userErrors?.length) {
          clearTimeout(safety);
          fail(result.userErrors[0]?.message);
          return;
        }
        /* Keyboard users: once Horizon has re-rendered the rows, land on the newly added line (it is first). */
        if (hadFocus) {
          const start = Date.now();
          const refocus = () => {
            const active = doc.activeElement;
            const lost = !active || active === doc.body || !active.isConnected || active.matches?.('.theme-drawer__close-button');
            if (!btn.isConnected || Date.now() - start > 2500) {
              if (lost) dialog()?.querySelector('.cart-items__title')?.focus();
              return;
            }
            setTimeout(refocus, 120);
          };
          setTimeout(refocus, 120);
        }
      })
      .catch((error) => {
        clearTimeout(safety);
        if (error?.name === 'AbortError') return reset();
        fail();
      })
      .finally(settleStay);
  });

  window.BubboCart = { spring };
  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', observe, { once: true });
  else observe();
  doc.addEventListener('shopify:section:load', () => {
    observed = null;
    observe();
  });
})();
